// Deck flooring — two tabs:
//   • Tracker — the website-only per-vessel 3-stage tracker (vessels from the Overview, stage state in
//     D1). A "select mode" lets you tick several vessels and fire the "deck cap fitted" notification for
//     all of them at once (the same email the 3rd stage sends automatically).
//   • Sheet — the deck-flooring Smartsheet mirrored as a native editable grid, like Service and GRP.

import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { DECK_STAGES, parseDeliveryDate, type DeckStatus } from '@technohull/shared';
import {
  useDeck, useOverview, useSetDeckStage, useDeckNotify,
  useDeckGrid, useDeckGridWrite, useDeckAddRow, useDeckDeleteRow,
  type DeckResponse, type SheetGridColumn, type SheetGridRow, type ServiceCellValue,
} from '../features/hooks.js';
import { useNameResolver } from '../features/directory.js';
import { useAuth } from '../auth/AuthContext.js';
import { ActionBar, ErrorState, Fact, FactEnd, FactSentence, FilterBar, InlineError, LoadingState, PageHeader, Pill, SearchInput, Segmented, SyncLabel, useConfirm } from '../components/ui.js';
import { useCellHistoryHover } from '../components/CellHistory.js';
import { useDateFormatter, useFormatDate, isStatusValue, statusPillKind } from '../lib/format.js';
import { useIsPhone } from '../lib/media.js';
import { IconCheck, IconHourglass, IconPlay, IconChevronRight, IconTrash } from '../components/icons.js';
import { SortMark } from '../components/SortMark.js';
import { fold, hasText } from '../lib/search.js';
import { sortRows, useTableSort, type SortDir, type SortState } from '../lib/tableSort.js';

const STATUS_LABEL: Record<DeckStatus, string> = { pending: 'Pending', progress: 'In progress', done: 'Done' };
const STATUS_ORDER: DeckStatus[] = ['pending', 'progress', 'done'];
const STAGE_SHORT = ['Added to GRP', 'GRP green', 'Deck cap'];
const shortLabel = (i: number) => STAGE_SHORT[i] ?? DECK_STAGES[i] ?? `Stage ${i + 1}`;

const statusIcon = (s: DeckStatus, size = 13) =>
  s === 'done' ? <IconCheck size={size} /> : s === 'progress' ? <IconPlay size={size} /> : <IconHourglass size={size} />;

const todayISO = () => {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

type Progress = 'notStarted' | 'inProgress' | 'complete';

// the tracker's order: a key picked in the Sort select plus a direction toggle beside it
type TrackerSort = 'name' | 'delivery' | 'progress';
const TRACKER_SORTS: TrackerSort[] = ['name', 'delivery', 'progress'];
const TRACKER_DEFAULT: SortState<TrackerSort> = { key: 'name', dir: 'asc' };
// what each direction means for each key, for the toggle's tooltip
const ORDER_WORDS: Record<TrackerSort, Record<SortDir, string>> = {
  name: { asc: 'Name A→Z', desc: 'Name Z→A' },
  delivery: { asc: 'Soonest delivery first', desc: 'Latest delivery first' },
  progress: { asc: 'Least done first', desc: 'Most done first' },
};

export function Deck() {
  const [view, setView] = useState<'tracker' | 'sheet'>('tracker');
  return (
    <>
      <PageHeader title="Deck Flooring">
        <Segmented value={view} onChange={setView} ariaLabel="View" options={[{ value: 'tracker', label: 'Tracker' }, { value: 'sheet', label: 'Sheet' }]} />
      </PageHeader>
      {view === 'tracker' ? <DeckTracker /> : <DeckSheet />}
    </>
  );
}

// ── Tracker tab ──────────────────────────────────────────────────────────────────
function DeckTracker() {
  const overview = useOverview();
  const deck = useDeck();
  const setStage = useSetDeckStage();
  const notify = useDeckNotify();
  const nameOf = useNameResolver();
  const fmtDate = useFormatDate();
  const { can } = useAuth();
  const canWrite = can('overview:write');
  const isPhone = useIsPhone();

  const [q, setQ] = useState('');
  const [delivery, setDelivery] = useState<'current' | 'all' | 'delivered'>('current');
  const [model, setModel] = useState('all');
  const [progress, setProgress] = useState<'all' | Progress>('all');
  // kept for this tab like the other sortable tables; anything unreadable falls back to name A→Z
  const { sort: storedSort, setSort } = useTableSort<TrackerSort>('deck-tracker', TRACKER_DEFAULT);
  const sort = storedSort && TRACKER_SORTS.includes(storedSort.key) ? storedSort : TRACKER_DEFAULT;
  const [openSap, setOpenSap] = useState<Set<string>>(new Set());

  // select mode: tick vessels, then fire the deck-cap notification for all of them.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifyErr, setNotifyErr] = useState(false);

  const stateBy = useMemo(() => {
    const m = new Map<string, DeckResponse['items'][number]>();
    for (const it of deck.data?.items ?? []) m.set(`${it.sap}:${it.stageNo}`, it);
    return m;
  }, [deck.data]);

  const statusOf = (sap: string, stageNo: number): DeckStatus => stateBy.get(`${sap}:${stageNo}`)?.status ?? 'pending';

  const vesselProgress = (sap: string): { done: number; anyProgress: boolean; kind: Progress } => {
    let done = 0;
    let anyProgress = false;
    for (let i = 0; i < DECK_STAGES.length; i++) {
      const s = statusOf(sap, i);
      if (s === 'done') done++;
      else if (s === 'progress') anyProgress = true;
    }
    const kind: Progress = done >= DECK_STAGES.length ? 'complete' : done > 0 || anyProgress ? 'inProgress' : 'notStarted';
    return { done, anyProgress, kind };
  };

  const models = useMemo(() => {
    const set = new Set<string>();
    for (const v of overview.data?.vessels ?? []) if (v.name && v.model) set.add(v.model);
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [overview.data]);

  const vessels = useMemo(() => {
    let vs = (overview.data?.vessels ?? []).filter((v) => v.name && v.sap);
    if (delivery === 'current') vs = vs.filter((v) => !v.left);
    else if (delivery === 'delivered') vs = vs.filter((v) => v.left);
    if (model !== 'all') vs = vs.filter((v) => v.model === model);
    // accents, the Greek tonos and a final ς don't matter
    const query = fold(q.trim());
    if (query) vs = vs.filter((v) => hasText(query, v.name, v.sap, v.model));
    if (progress !== 'all') vs = vs.filter((v) => vesselProgress(v.sap).kind === progress);
    // the shared table sort (blanks last either way, Greek + English name order). Name A→Z first, so
    // vessels level on the chosen key keep name order whichever way it runs.
    vs = sortRows(vs, TRACKER_DEFAULT, (v) => v.name);
    if (sort.key !== 'name' || sort.dir !== 'asc') {
      vs = sortRows(vs, sort, (v, key) =>
        key === 'progress' ? vesselProgress(v.sap).done
        // a real date sorts as a date; free text after the dates; no date last
        : key === 'delivery' ? parseDeliveryDate(v)?.getTime() ?? v.deliveryDate
        : v.name);
    }
    return vs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overview.data, delivery, model, q, progress, sort, stateBy]);

  const stats = useMemo(() => {
    const s = { total: vessels.length, notStarted: 0, inProgress: 0, complete: 0, perStage: DECK_STAGES.map(() => 0) };
    for (const v of vessels) {
      const p = vesselProgress(v.sap);
      if (p.kind === 'complete') s.complete++;
      else if (p.kind === 'inProgress') s.inProgress++;
      else s.notStarted++;
      for (let i = 0; i < DECK_STAGES.length; i++) if (statusOf(v.sap, i) === 'done') s.perStage[i]!++;
    }
    return s;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vessels, stateBy]);

  const toggleOpen = (sap: string) =>
    setOpenSap((p) => { const n = new Set(p); n.has(sap) ? n.delete(sap) : n.add(sap); return n; });
  const allOpen = vessels.length > 0 && vessels.every((v) => openSap.has(v.sap));
  const toggleAll = () => setOpenSap(allOpen ? new Set() : new Set(vessels.map((v) => v.sap)));

  const toggleSel = (sap: string) =>
    setSelected((p) => { const n = new Set(p); n.has(sap) ? n.delete(sap) : n.add(sap); return n; });
  const selectAllFiltered = () => setSelected(new Set(vessels.map((v) => v.sap)));
  const clearSel = () => setSelected(new Set());

  const sendNotify = () => {
    const saps = [...selected];
    if (!saps.length) return;
    setNotifyMsg('');
    notify.mutate({ saps }, {
      onSuccess: (r) => {
        setNotifyErr(!r.ok);
        setNotifyMsg(r.ok
          ? `Deck-cap notification sent for ${r.triggered} vessel${r.triggered === 1 ? '' : 's'} to ${r.recipients} recipient${r.recipients === 1 ? '' : 's'}.`
          : (r.reason ?? 'Could not send the notification.'));
        if (r.ok) { clearSel(); setSelectMode(false); }
      },
      onError: () => { setNotifyErr(true); setNotifyMsg('Could not send the notification.'); },
    });
  };

  const set = (sap: string, stageNo: number, patch: Partial<{ status: DeckStatus; comment: string; date: string }>) => {
    const cur = stateBy.get(`${sap}:${stageNo}`);
    setStage.mutate({
      sap,
      stageNo,
      status: patch.status ?? cur?.status ?? 'pending',
      comment: patch.comment ?? cur?.comment ?? '',
      date: patch.date ?? cur?.date ?? '',
    });
  };

  const advance = (sap: string, stageNo: number) => {
    const cur = statusOf(sap, stageNo);
    if (cur === 'done') { if (!openSap.has(sap)) toggleOpen(sap); return; }
    const next: DeckStatus = cur === 'pending' ? 'progress' : 'done';
    set(sap, stageNo, { status: next, ...(next === 'done' && !stateBy.get(`${sap}:${stageNo}`)?.date ? { date: todayISO() } : {}) });
  };

  const busy = overview.isLoading || deck.isLoading;
  const total = DECK_STAGES.length;
  const activeFilters = (q.trim() ? 1 : 0) + (delivery !== 'current' ? 1 : 0) + (model !== 'all' ? 1 : 0) + (progress !== 'all' ? 1 : 0);
  const clearFilters = () => { setQ(''); setDelivery('current'); setModel('all'); setProgress('all'); };

  // view actions: inside the filter bar on wider screens; on phones in their own row under it, so they
  // never fold behind "Filters"
  const actions = (
    <>
      <button className="btn btn-sm" onClick={toggleAll} disabled={!vessels.length}>{allOpen ? 'Collapse all' : 'Expand all'}</button>
      {canWrite && (
        <button
          className={`btn btn-sm${selectMode ? ' btn-primary' : ''}`}
          onClick={() => { setSelectMode((s) => !s); clearSel(); setNotifyMsg(''); }}
          title="Select several vessels and send the deck-cap notification"
        >{selectMode ? 'Done selecting' : 'Select to notify'}</button>
      )}
      <button className="btn btn-sm" disabled={overview.isFetching || deck.isFetching} onClick={() => { void overview.refetch(); void deck.refetch(); }}>
        <SyncLabel busy={overview.isFetching || deck.isFetching} />
      </button>
    </>
  );
  const funnel = (
    <div className="deck-funnel">
      {DECK_STAGES.map((stage, i) => {
        const n = stats.perStage[i] ?? 0;
        const pct = stats.total ? Math.round((n / stats.total) * 100) : 0;
        return (
          <div className="deck-fn" key={i} title={`${n} of ${stats.total} vessels have completed “${stage}”`}>
            <div className="deck-fn-hd"><span>{shortLabel(i)}</span><span className="deck-fn-n">{n}/{stats.total}</span></div>
            <div className="deck-prog"><span style={{ width: `${pct}%` }} /></div>
          </div>
        );
      })}
    </div>
  );

  return (
    <>
      <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder={isPhone ? 'Search vessels' : 'Search vessel / SAP / model'} width={210} />} activeCount={activeFilters} onClear={clearFilters}>
        <select className="field" value={delivery} onChange={(e) => setDelivery(e.target.value as 'current' | 'all' | 'delivered')} title="Delivery status">
          <option value="current">Current production</option>
          <option value="all">All</option>
          <option value="delivered">Delivered</option>
        </select>
        <select className="field" value={model} onChange={(e) => setModel(e.target.value)} title="Model">
          <option value="all">All models</option>
          {models.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="field" value={progress} onChange={(e) => setProgress(e.target.value as 'all' | Progress)} title="Progress">
          <option value="all">Any progress</option>
          <option value="notStarted">Not started</option>
          <option value="inProgress">In progress</option>
          <option value="complete">Complete</option>
        </select>
        {/* the order and its direction stay side by side when the bar wraps */}
        <span className="deck-sort">
          <select className="field" value={sort.key} onChange={(e) => setSort({ key: e.target.value as TrackerSort, dir: sort.dir })} title="Sort">
            <option value="name">Sort: name</option>
            <option value="delivery">Sort: delivery</option>
            <option value="progress">Sort: progress</option>
          </select>
          <button
            type="button"
            className="btn btn-sm deck-sortdir"
            onClick={() => setSort({ key: sort.key, dir: sort.dir === 'asc' ? 'desc' : 'asc' })}
            aria-label={sort.dir === 'asc' ? 'Sort ascending' : 'Sort descending'}
            title={`${ORDER_WORDS[sort.key][sort.dir]}. Click to reverse.`}
          ><SortMark dir={sort.dir} /></button>
        </span>
        {!isPhone && actions}
      </FilterBar>
      {isPhone && <div className="deck-tools">{actions}</div>}

      {/* a successful send leaves select mode (unmounting the bar above), so the result shows here too */}
      {!selectMode && notifyMsg && (
        <div className="muted" role="status" style={{ margin: '0 0 12px', fontSize: 12.5, color: notifyErr ? 'var(--err-fg)' : undefined }}>{notifyMsg}</div>
      )}
      {selectMode && !isPhone && (
        <div className="deck-selbar">
          <span className="deck-selcount">{selected.size} selected</span>
          <button className="btn btn-sm" onClick={selectAllFiltered} disabled={!vessels.length}>Select all shown</button>
          <button className="btn btn-sm" onClick={clearSel} disabled={!selected.size}>Clear</button>
          <span style={{ flex: 1 }} />
          {notifyMsg && <span className="muted" role="status" style={{ fontSize: 12.5, color: notifyErr ? 'var(--err-fg)' : undefined }}>{notifyMsg}</span>}
          <button className="btn btn-sm btn-primary" onClick={sendNotify} disabled={!selected.size || notify.isPending}>
            {notify.isPending ? 'Sending…' : 'Notify: deck cap fitted'}
          </button>
        </div>
      )}

      {busy && <LoadingState label="Loading…" />}
      {overview.error && <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />}
      {deck.error && <ErrorState error={deck.error} onRetry={() => void deck.refetch()} />}
      {setStage.isError && <InlineError error={setStage.error} onDismiss={() => setStage.reset()} />}

      {overview.data && deck.data && (
        <>
          {/* the headline: the filtered fleet as one sentence, each state in its status colour (the legend's icons) */}
          <FactSentence className="deck-facts">
            <Fact>{stats.total} {stats.total === 1 ? 'vessel' : 'vessels'}</Fact> on deck flooring<FactEnd>:</FactEnd>{' '}
            <Fact icon={statusIcon('pending')} title="No stage started yet">{stats.notStarted} not started</Fact><FactEnd>,</FactEnd>{' '}
            <Fact tone="info" icon={statusIcon('progress')} title="At least one stage started or done, not all three">{stats.inProgress} in progress</Fact>{' '}
            and <Fact tone="ok" icon={statusIcon('done')} title="All three stages done">{stats.complete} complete</Fact><FactEnd>.</FactEnd>
          </FactSentence>
          <div className="deck-summary">
            {isPhone ? (
              <details className="deck-totals">
                <summary><IconChevronRight size={14} className="deck-totals-chev" />Stage totals</summary>
                {funnel}
              </details>
            ) : funnel}
          </div>

          <div className="deck-legend">
            <span className="deck-lg pending">{statusIcon('pending', 12)} Pending</span>
            <span className="deck-lg progress">{statusIcon('progress', 12)} In progress</span>
            <span className="deck-lg done">{statusIcon('done', 12)} Done</span>
            <span className="deck-legend-hint">
              {canWrite ? (isPhone ? 'Tap a stage on a row to advance it. ' : 'Click a stage on a row to advance it. ') : ''}The GRP stages and “Deck cap fitted” fill in automatically from Smartsheet (GRP and the Production deck-cap task).
            </span>
          </div>

          <div className="deck-list">
            {vessels.map((v) => {
              const { done } = vesselProgress(v.sap);
              const open = openSap.has(v.sap);
              const pct = Math.round((done / total) * 100);
              const sel = selected.has(v.sap);
              return (
                <div key={v.sap} className={`deck-card${open ? ' is-open' : ''}${sel ? ' is-selected' : ''}`}>
                  <div className="deck-hd">
                    {selectMode && (
                      <label className="deck-selhit hit-44">
                        <input
                          type="checkbox"
                          className="deck-selcb"
                          checked={sel}
                          onChange={() => toggleSel(v.sap)}
                          onClick={(e) => e.stopPropagation()}
                          aria-label={`Select ${v.name}`}
                        />
                      </label>
                    )}
                    <button className={`deck-chev${open ? ' is-open' : ''}`} onClick={() => toggleOpen(v.sap)} aria-label={`${open ? 'Collapse' : 'Expand'} ${v.name}`} aria-expanded={open}>
                      <IconChevronRight size={16} />
                    </button>
                    <div className="deck-idmeta" onClick={() => (selectMode ? toggleSel(v.sap) : toggleOpen(v.sap))}>
                      <div className="deck-title">{v.name}</div>
                      <div className="deck-meta">
                        {v.sap} · {v.model}{v.deliveryDate ? ` · DEL ${parseDeliveryDate(v) ? fmtDate(v.deliveryDate) : v.deliveryDate}` : ''}{v.left ? ' · delivered' : ''}
                      </div>
                    </div>
                    <div className="deck-stages">
                      {DECK_STAGES.map((stage, i) => {
                        const s = statusOf(v.sap, i);
                        return (
                          <button
                            key={i}
                            type="button"
                            className={`deck-seg ${s}`}
                            disabled={!canWrite || selectMode}
                            title={canWrite ? (s === 'done' ? `${stage}: done - click to open` : `${stage}: ${STATUS_LABEL[s]} - click to mark ${s === 'pending' ? 'in progress' : 'done'}`) : `${stage}: ${STATUS_LABEL[s]}`}
                            onClick={(e) => { e.stopPropagation(); advance(v.sap, i); }}
                          >
                            <span className="deck-seg-ic">{statusIcon(s)}</span>
                            <span className="deck-seg-lb">{shortLabel(i)}</span>
                            <span className="sr-only">: {STATUS_LABEL[s]}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div className="deck-progwrap">
                      <span className={`deck-count${done >= total ? ' is-done' : ''}`}>{done}/{total}</span>
                      <div className="deck-prog"><span className={done >= total ? 'is-done' : ''} style={{ width: `${pct}%` }} /></div>
                    </div>
                  </div>

                  {open && !selectMode && (
                    <div className="deck-body">
                      {DECK_STAGES.map((stage, i) => {
                        const st = stateBy.get(`${v.sap}:${i}`);
                        const status = st?.status ?? 'pending';
                        return (
                          <div className="deck-stage" key={i}>
                            <div className="deck-stage-top">
                              <span className="deck-stage-name">{stage}</span>
                              {canWrite ? (
                                <div className="deck-setbtns" role="group" aria-label={`${stage} status`}>
                                  {STATUS_ORDER.map((s) => (
                                    <button
                                      key={s}
                                      type="button"
                                      className={`deck-setbtn ${s}${status === s ? ' on' : ''}`}
                                      onClick={() => set(v.sap, i, { status: s, ...(s === 'done' && !st?.date ? { date: todayISO() } : {}) })}
                                    >
                                      {statusIcon(s, 12)} {STATUS_LABEL[s]}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <span className={`deck-seg ${status} is-static`}>{statusIcon(status)} {STATUS_LABEL[status]}</span>
                              )}
                            </div>
                            <div className="deck-stage-fields">
                              <label className="deck-fld">
                                <span>Comment</span>
                                {canWrite ? (
                                  <input
                                    key={st?.comment ?? ''}
                                    className="field"
                                    defaultValue={st?.comment ?? ''}
                                    placeholder="Add a note…"
                                    onBlur={(e) => { if (e.target.value !== (st?.comment ?? '')) set(v.sap, i, { comment: e.target.value }); }}
                                  />
                                ) : (
                                  <span className="deck-ro">{st?.comment || '-'}</span>
                                )}
                              </label>
                              <label className="deck-fld deck-fld-date">
                                <span>Date</span>
                                {canWrite ? (
                                  <span className="deck-datewrap">
                                    <input className="field" type="date" value={st?.date ?? ''} onChange={(e) => set(v.sap, i, { date: e.target.value })} />
                                    {!st?.date && <button type="button" className="btn btn-sm btn-ghost" onClick={() => set(v.sap, i, { date: todayISO() })}>Today</button>}
                                  </span>
                                ) : (
                                  <span className="deck-ro">{fmtDate(st?.date) || st?.date || '-'}</span>
                                )}
                              </label>
                            </div>
                            {st?.by && (
                              <div className="deck-stage-who">
                                {st.by === 'system'
                                  ? `Filled in automatically from Smartsheet${st.at ? ` · ${fmtDate(st.at)}` : ''}`
                                  : `Last updated by ${nameOf(st.by)}${st.at ? ` · ${fmtDate(st.at)}` : ''}`}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}

            {vessels.length === 0 && (
              <div className="deck-empty">No vessels match the current filters.</div>
            )}
          </div>
        </>
      )}
      {/* phones: the select bar rides at the bottom, so Notify stays in reach however far the list scrolls */}
      <ActionBar
        show={selectMode}
        message={notifyMsg ? <span role="status" style={{ color: notifyErr ? 'var(--err-fg)' : undefined }}>{notifyMsg}</span> : `${selected.size} selected`}
      >
        <button className="btn btn-sm" onClick={selectAllFiltered} disabled={!vessels.length}>Select all</button>
        <button className="btn btn-sm" onClick={clearSel} disabled={!selected.size}>Clear</button>
        <button className="btn btn-sm btn-primary" onClick={sendNotify} disabled={!selected.size || notify.isPending} title="Notify: deck cap fitted">
          {notify.isPending ? 'Sending…' : 'Notify'}
        </button>
      </ActionBar>
    </>
  );
}

// ── Sheet tab: the deck-flooring Smartsheet as a native editable grid (mirrors GRP's Sheet view) ──────
const cellStr = (v: string | boolean | undefined): string =>
  v === true ? '✓' : v === false ? '' : v == null ? '' : String(v);
// Smartsheet's symbol balls drawn in the app's status fills, so they follow the theme and colour profile
const DOT: Record<string, string> = { green: 'var(--ok-fill)', yellow: 'var(--warn-fill)', red: 'var(--err-fill)', blue: 'var(--info-fill)', gray: 'var(--text-3)', grey: 'var(--text-3)' };
const stopEvt = (e: MouseEvent) => e.stopPropagation();

function DeckSheet() {
  const df = useDateFormatter();
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useDeckGrid();
  const { can } = useAuth();
  const canWrite = can('overview:write');
  const write = useDeckGridWrite();
  const addRow = useDeckAddRow();
  const delRow = useDeckDeleteRow();
  const { onEnter: histEnter, onLeave: histLeave, bindPress: histPress, popover: histPopover } = useCellHistoryHover(data?.sheetId);
  const [confirmNode, confirm] = useConfirm();
  const isPhone = useIsPhone();

  const [q, setQ] = useState('');
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [everExpanded, setEverExpanded] = useState(false);
  const [showEmpty, setShowEmpty] = useState(false);
  const [editing, setEditing] = useState<{ rowId: number; colId: number } | null>(null);
  const [draft, setDraft] = useState('');
  // a header click sorts the top-level rows (A→Z, Z→A, back to the sheet order); rows inside a row
  // stay under it in the sheet's order
  const { sort: pickedSort, dirOf, th: sortTh } = useTableSort<number>('deck-sheet');

  const columns = data?.columns ?? [];
  const allRows = data?.rows ?? [];
  const primaryId = useMemo(() => (columns.find((c) => c.primary) ?? columns[0])?.id, [columns]);
  const rowName = (r: SheetGridRow) => cellStr(r.cells[primaryId ?? -1]);

  const { childrenOf, depthOf, hasKids, topLevel } = useMemo(() => {
    const byId = new Map(allRows.map((r) => [r.id, r]));
    const kids = new Map<number, SheetGridRow[]>();
    const top: SheetGridRow[] = [];
    for (const r of allRows) {
      if (r.parentId != null && byId.has(r.parentId)) {
        const arr = kids.get(r.parentId);
        if (arr) arr.push(r); else kids.set(r.parentId, [r]);
      } else top.push(r);
    }
    const depthCache = new Map<number, number>();
    const depthOf = (r: SheetGridRow): number => {
      if (depthCache.has(r.id)) return depthCache.get(r.id)!;
      let d = 0; let cur: SheetGridRow | undefined = r;
      while (cur && cur.parentId != null) { const p = byId.get(cur.parentId); if (!p) break; d++; cur = p; }
      depthCache.set(r.id, d); return d;
    };
    return { childrenOf: (id: number) => kids.get(id) ?? [], depthOf, hasKids: (id: number) => kids.has(id), topLevel: top };
  }, [allRows]);

  const emptyColIds = useMemo(() => {
    const nonEmpty = new Set<number>();
    for (const r of allRows) for (const c of columns) {
      if (nonEmpty.has(c.id)) continue;
      const v = r.cells[c.id];
      if (c.type === 'CHECKBOX' ? v === true : cellStr(v) !== '') nonEmpty.add(c.id);
    }
    return new Set(columns.filter((c) => c.id !== primaryId && !nonEmpty.has(c.id)).map((c) => c.id));
  }, [allRows, columns, primaryId]);

  const cols = useMemo(() => (showEmpty ? columns : columns.filter((c) => !emptyColIds.has(c.id))), [columns, emptyColIds, showEmpty]);
  // a remembered column the sheet no longer has reads as no sort
  const sort = pickedSort && columns.some((c) => c.id === pickedSort.key) ? pickedSort : null;
  const sortedTop = useMemo(() => sortRows(topLevel, sort, (r, key) => r.cells[key]), [topLevel, sort]);

  const countDesc = useMemo(() => {
    const cache = new Map<number, number>();
    const f = (id: number): number => {
      if (cache.has(id)) return cache.get(id)!;
      let n = 0; for (const ch of childrenOf(id)) n += 1 + f(ch.id);
      cache.set(id, n); return n;
    };
    return f;
  }, [childrenOf]);

  // accents, the Greek tonos and a final ς don't matter; a tick never matches the word "true"
  const query = fold(q.trim());
  const searchKeep = useMemo(() => {
    if (!query) return null;
    const byId = new Map(allRows.map((r) => [r.id, r]));
    const keep = new Set<number>();
    for (const r of allRows) {
      if (!Object.values(r.cells).some((c) => hasText(query, c))) continue;
      let cur: SheetGridRow | undefined = r;
      while (cur) { keep.add(cur.id); cur = cur.parentId != null ? byId.get(cur.parentId) : undefined; }
    }
    return keep;
  }, [allRows, query]);

  const visible = useMemo(() => {
    const out: SheetGridRow[] = [];
    const walk = (rows: SheetGridRow[]) => {
      for (const r of rows) {
        if (searchKeep && !searchKeep.has(r.id)) continue;
        out.push(r);
        if (hasKids(r.id) && (searchKeep || !collapsed.has(r.id))) walk(childrenOf(r.id));
      }
    };
    walk(sortedTop);
    return out;
  }, [sortedTop, childrenOf, hasKids, collapsed, searchKeep]);

  // first load → collapse every parent so the initial view is the top-level list.
  const inited = useRef(false);
  useEffect(() => {
    if (inited.current || allRows.length === 0) return;
    inited.current = true;
    const n = new Set<number>();
    for (const r of allRows) if (hasKids(r.id)) n.add(r.id);
    setCollapsed(n);
  }, [allRows, hasKids]);

  const toggle = (id: number) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const expand = (id: number) => setCollapsed((s) => { const n = new Set(s); n.delete(id); return n; });
  const collapseAll = () => { const n = new Set<number>(); for (const r of allRows) if (hasKids(r.id)) n.add(r.id); setCollapsed(n); setEverExpanded(false); };
  const expandAll = () => { setCollapsed(new Set()); setEverExpanded(true); };

  const isSymbolCol = (c: SheetGridColumn) => !!c.symbol;
  const editingHere = (r: SheetGridRow, c: SheetGridColumn) => editing?.rowId === r.id && editing?.colId === c.id;
  const writeCell = (rowId: number, colId: number, value: ServiceCellValue) => write.mutate({ rowId, columnId: colId, value });
  const beginEdit = (r: SheetGridRow, c: SheetGridColumn, e: { stopPropagation(): void }) => { e.stopPropagation(); setEditing({ rowId: r.id, colId: c.id }); setDraft(cellStr(r.cells[c.id])); };
  // keyboard twin of the click-to-edit spans (Enter / Space, like a button)
  const editKey = (r: SheetGridRow, c: SheetGridColumn) => (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); beginEdit(r, c, e); }
  };
  const commitText = (r: SheetGridRow, c: SheetGridColumn) => { if (draft !== cellStr(r.cells[c.id])) writeCell(r.id, c.id, draft === '' ? null : draft); setEditing(null); };

  const addTop = () => { if (primaryId != null) addRow.mutate({ cells: [{ columnId: primaryId, value: '' }] }); };
  const addChild = (r: SheetGridRow) => { if (primaryId == null) return; addRow.mutate({ cells: [{ columnId: primaryId, value: '' }], parentId: r.id }); expand(r.id); };
  const removeRow = async (r: SheetGridRow) => {
    const name = cellStr(r.cells[primaryId ?? -1]) || 'this row';
    const kids = countDesc(r.id);
    const msg = kids > 0 ? `Delete "${name}" and its ${kids} row(s) below it?` : `Delete "${name}"?`;
    if (await confirm({ title: 'Delete row', message: msg, confirmLabel: 'Delete', danger: true })) delRow.mutate({ rowId: r.id });
  };

  const renderView = (c: SheetGridColumn, r: SheetGridRow): ReactNode => {
    const v = r.cells[c.id];
    const s = cellStr(v);
    // Yes / No / Hold (a decision-symbol column like Customer OK included) as the Overview grid reads them:
    // Yes ok green, Hold warn amber, No a neutral pill
    if (isStatusValue(s)) return <Pill kind={statusPillKind(s)}>{s}</Pill>;
    if (isSymbolCol(c)) {
      const dot = DOT[String(v ?? '').trim().toLowerCase()];
      if (dot) return <span className="grp-dot" style={{ background: dot }} title={String(v)} />;
      return s || null;
    }
    if (c.type === 'DATE' || c.type === 'DATETIME') return df.cell(c.type, v) || null; // in the user's date format
    return s || null;
  };

  const cellContent = (c: SheetGridColumn, r: SheetGridRow): ReactNode => {
    const v = r.cells[c.id];
    if (c.type === 'CHECKBOX') {
      if (canWrite) return <input type="checkbox" className="grp-cb" checked={v === true} onClick={stopEvt} onChange={(e) => writeCell(r.id, c.id, e.currentTarget.checked)} />;
      return v === true ? <span className="grp-check"><IconCheck size={13} /></span> : null;
    }
    if (canWrite && editingHere(r, c)) {
      if ((isSymbolCol(c) || c.type === 'PICKLIST') && c.options.length) {
        return (
          <select className="grp-edit" autoFocus value={draft} onClick={stopEvt}
            onChange={(e) => { const val = e.currentTarget.value; writeCell(r.id, c.id, val === '' ? null : val); setEditing(null); }}
            onBlur={() => setEditing(null)}>
            <option value="" />
            {c.options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      }
      const isDate = c.type === 'DATE' || c.type === 'DATETIME';
      return (
        <input className="grp-edit" autoFocus type={isDate ? 'date' : 'text'} value={draft} onClick={stopEvt}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onBlur={() => commitText(r, c)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitText(r, c); } else if (e.key === 'Escape') { e.preventDefault(); setEditing(null); } }} />
      );
    }
    const vw = renderView(c, r);
    if (canWrite) {
      return (
        <span className="grp-editable" role="button" tabIndex={0} aria-label={vw == null ? `Edit ${c.title}` : undefined} onClick={(e) => beginEdit(r, c, e)} onKeyDown={editKey(r, c)} title="Click to edit">
          {vw ?? <span className="grp-empty" />}
        </span>
      );
    }
    return vw;
  };

  const lastSync = dataUpdatedAt ? df.time(dataUpdatedAt) : null;
  const hiddenCount = columns.length - cols.length;

  const expandBtn = <button className="btn btn-sm" onClick={everExpanded ? collapseAll : expandAll}>{everExpanded ? 'Collapse all' : 'Expand all'}</button>;
  const columnsBtn = (hiddenCount > 0 || showEmpty) && (
    <button className="btn btn-sm" onClick={() => setShowEmpty((s) => !s)} title="Blank columns are hidden to reduce scrolling">
      {showEmpty ? 'Hide empty columns' : `Show all columns${hiddenCount ? ` (+${hiddenCount})` : ''}`}
    </button>
  );
  const rowSync = (
    <>
      {canWrite && <button className="btn btn-sm btn-primary" onClick={addTop} disabled={addRow.isPending}>+ Row</button>}
      {lastSync && <span className="muted">Synced {lastSync}</span>}
      <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} /></button>
    </>
  );

  return (
    <>
      {/* phones: only the column filter folds behind "Filters"; the actions keep their own row */}
      <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder="Search the sheet" width={210} />} activeCount={q.trim() ? 1 : 0} onClear={() => setQ('')}>
        {!isPhone && expandBtn}
        {columnsBtn}
        {!isPhone && rowSync}
      </FilterBar>
      {isPhone && <div className="deck-tools">{expandBtn}{rowSync}</div>}

      {isLoading && <LoadingState label="Loading deck sheet…" />}
      {/* a failed background refetch keeps the grid usable: inline error, cleared by the next Sync */}
      {error && !data && <ErrorState error={error} onRetry={() => void refetch()} />}
      {error && data && <InlineError error={error} />}
      {write.isError && <InlineError error={write.error} onDismiss={() => write.reset()} />}
      {addRow.isError && <InlineError error={addRow.error} onDismiss={() => addRow.reset()} />}
      {delRow.isError && <InlineError error={delRow.error} onDismiss={() => delRow.reset()} />}

      {data && (
        <div className="tbl-wrap is-grid grid-fill">
          <table className="tbl grp-tbl">
            <thead>
              <tr>
                {cols.map((c) => <th key={c.id} {...sortTh(c.id, c.id === primaryId ? 'grp-name-col' : undefined)}>{c.title}<SortMark dir={dirOf(c.id)} /></th>)}
                {canWrite && <th className="grp-actions-col" aria-label="Row actions" />}
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const depth = depthOf(r);
                const canToggle = hasKids(r.id);
                const isCollapsed = canToggle && !searchKeep && collapsed.has(r.id);
                const tint = r.fmt ? (r.fmt[String(primaryId)] ?? Object.values(r.fmt)[0]) : undefined;
                const tdStyle = tint ? { background: tint, color: '#1a1a1a' } : undefined;
                const rowClass = [depth === 0 ? 'grp-row-top' : '', canToggle ? 'grp-row-parent' : ''].filter(Boolean).join(' ');
                const editingName = canWrite && editing?.rowId === r.id && editing?.colId === primaryId;
                const badge = isCollapsed && countDesc(r.id) > 0 ? <span className="grp-count" title={`${countDesc(r.id)} rows`}>{countDesc(r.id)}</span> : null;
                return (
                  <tr
                    key={r.id}
                    className={rowClass}
                    style={tint ? { background: tint } : undefined}
                    // mouse convenience only — the twist button below is the keyboard / screen-reader control,
                    // so the row keeps its table semantics and its inner controls stay reachable
                    onClick={canToggle ? () => toggle(r.id) : undefined}
                  >
                    {cols.map((c) => {
                      if (c.id === primaryId) {
                        return (
                          <td key={c.id} className="grp-name-col" style={{ ...tdStyle, paddingLeft: 8 + depth * 18 }}>
                            <span className="grp-name-inner">
                              {canToggle ? (
                                <button
                                  type="button"
                                  className="grp-twist"
                                  aria-expanded={!isCollapsed}
                                  aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${rowName(r) || 'row'}`}
                                  onClick={(e) => { e.stopPropagation(); toggle(r.id); }}
                                >{isCollapsed ? '+' : '−'}</button>
                              ) : <span className="grp-twist-spacer" />}
                              {editingName ? (
                                <input className="grp-edit" autoFocus value={draft} onClick={stopEvt}
                                  onChange={(e) => setDraft(e.currentTarget.value)}
                                  onBlur={() => commitText(r, c)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitText(r, c); } else if (e.key === 'Escape') { e.preventDefault(); setEditing(null); } }} />
                              ) : (
                                <span
                                  className={`${depth === 0 ? 'grp-name-boat' : ''}${canWrite ? ' grp-editable' : ''}`}
                                  onClick={canWrite ? (e) => beginEdit(r, c, e) : undefined}
                                  {...(canWrite
                                    ? { role: 'button' as const, tabIndex: 0, onKeyDown: editKey(r, c), 'aria-label': cellStr(r.cells[c.id]) ? undefined : `Edit ${c.title}` }
                                    : {})}
                                >
                                  {cellStr(r.cells[c.id]) || (canWrite ? <span className="grp-empty" /> : '')}
                                </span>
                              )}
                              {badge}
                            </span>
                          </td>
                        );
                      }
                      return (
                        <td key={c.id} className={c.type === 'CHECKBOX' || isSymbolCol(c) ? 'grp-c' : undefined} style={tdStyle}
                          onPointerEnter={histEnter(r.id, c.id, c.title, rowName(r))} onPointerLeave={histLeave}
                          {...histPress(r.id, c.id, c.title, rowName(r))}>
                          {cellContent(c, r)}
                        </td>
                      );
                    })}
                    {canWrite && (
                      <td className="grp-actions" style={tdStyle}>
                        <button className="grp-rowbtn" title="Add a row inside this one" aria-label={`Add a row inside ${rowName(r) || 'this row'}`} onClick={(e) => { e.stopPropagation(); addChild(r); }}>＋</button>
                        <button className="grp-rowbtn grp-rowbtn-del" title="Delete this row" aria-label={`Delete ${rowName(r) || 'this row'}`} onClick={(e) => { e.stopPropagation(); void removeRow(r); }}><IconTrash size={13} /></button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {/* under the grid, not centred in a row of a table wider than a phone */}
      {data && visible.length === 0 && (
        <div className="grid-empty muted">
          No matching rows.
          {query && <button type="button" className="btn btn-sm btn-ghost" onClick={() => setQ('')}>Clear filters</button>}
        </div>
      )}

      {histPopover}
      {confirmNode}
    </>
  );
}
